import type {
  AddToCartMetadata,
  BrowsingMetadata,
  ClickMetadata,
  HomePageViewMetadata,
  ImpressionMetadata,
  IPersonalizationStorageAdapter,
  PersonalizationEvent,
  PersonalizationEventPayload,
  PurchaseMetadata,
  ReferralMetadata,
  ResolveActorFn,
  RewardRedemptionMetadata,
  ReviewWriteMetadata,
  SendEventFn,
  SignupMetadata,
  ViewMetadata,
} from './types';
import type {
  AddToCartContext,
  BrowsingContext,
  ClickContext,
  DeviceType,
  HomePageViewContext,
  ImpressionContext,
  PurchaseContext,
  ReferralContext,
  RemoveFromCartContext,
  RewardRedemptionContext,
  ReviewWriteContext,
  SignupContext,
  ViewContext,
} from './constants';
import {
  DEVICE_TYPE,
  EVENT_QUEUE_MAX_SIZE,
  EVENT_TYPE,
  HIGH_VALUE_EVENT_TYPES,
  RETRY_CONFIG,
  SESSION_TIMEOUT_MS,
} from './constants';
import { getOrCreateSessionId } from './sessionManager';

/**
 * Callback function invoked when the actor ID changes.
 * @param {string} actorId - The new actor ID
 */
export type ActorIdChangeListener = (actorId: string) => void;

/**
 * Configuration options for PersonalizationClient.
 *
 * @property {ResolveActorFn} resolveActor - Function to resolve/fetch the actor (user) ID
 * @property {SendEventFn} sendEvent - Function to send events to the personalization API
 * @property {IPersonalizationStorageAdapter} storage - Storage adapter for persisting data
 * @property {boolean} [debug] - Enable debug mode (logs events to console instead of sending)
 * @property {boolean} [enabled] - Enable/disable tracking (default: true)
 * @property {DeviceType} [deviceType] - Device type override (auto-detected if not provided)
 * @property {boolean} [waitForExternalActorId] - Wait for external actor ID from window event
 * @property {boolean} [skipResolveWhenSessionValid] - Skip resolveActor call when stored actorId is present and session is still valid
 */
export interface PersonalizationClientConfig {
  resolveActor: ResolveActorFn;
  sendEvent: SendEventFn;
  storage: IPersonalizationStorageAdapter;
  debug?: boolean;
  enabled?: boolean;
  deviceType?: DeviceType;
  waitForExternalActorId?: boolean;
  /**
   * Skip background re-validation when local actorId exists and session is not expired.
   * Use this for web pages where actorId should be reused until session times out.
   */
  skipResolveWhenSessionValid?: boolean;
}

// TODO: Separate concerns into two classes:
//   - PersonalizationCoreClient: actorId lifecycle, event queue, retry, session management
//   - PersonalizationEventClient (extends core): convenience event methods (browsing, view, click, ...)
// This allows the core infrastructure to be tested and reused independently.

/**
 * Core client for tracking personalization events.
 *
 * Features:
 * - Automatic session management with 30-minute timeout
 * - Event queuing when actor ID is not yet available
 * - Auto-injection of common metadata (sessionId, deviceType)
 * - Debug mode for development/testing
 * - Platform-agnostic storage adapter pattern
 *
 * @class PersonalizationClient
 *
 * @example
 * // Basic setup
 * const client = new PersonalizationClient({
 *   resolveActor: async (token) => {
 *     const res = await fetch('/api/actors/resolve', {
 *       headers: { Authorization: `Bearer ${token}` }
 *     });
 *     return res.json();
 *   },
 *   sendEvent: async (payload) => {
 *     await fetch('/api/personalization/event', {
 *       method: 'POST',
 *       body: JSON.stringify(payload)
 *     });
 *   },
 *   storage: new WebPersonalizationStorageAdapter(),
 *   debug: process.env.NODE_ENV === 'development'
 * });
 *
 * // Initialize before tracking
 * await client.initialize();
 *
 * // Track events
 * client.view('product_123', 'product_detail', { position: 1 });
 * client.addToCart('product_123', 'cart', 2, { priceAtAdd: 49.99 });
 */
export class PersonalizationClient {
  private readonly resolveActorFn: ResolveActorFn;
  private readonly sendEventFn: SendEventFn;
  private readonly storage: IPersonalizationStorageAdapter;
  private readonly debug: boolean;
  private readonly enabled: boolean;
  private readonly deviceType: DeviceType;
  private readonly waitForExternalActorId: boolean;
  private readonly skipResolveWhenSessionValid: boolean;

  private actorId: string | null = null;
  private isInitialized = false;
  private initializePromise: Promise<void> | null = null;
  private eventQueue: Array<{ payload: PersonalizationEventPayload; resolve: () => void }> = [];
  private actorIdChangeListeners: ActorIdChangeListener[] = [];
  private reInitializeGeneration = 0;
  private actorRefreshInFlight = 0;
  private externalActorIdAbortController: AbortController | null = null;

  constructor(config: PersonalizationClientConfig) {
    this.resolveActorFn = config.resolveActor;
    this.sendEventFn = config.sendEvent;
    this.storage = config.storage;
    this.debug = config.debug ?? false;
    this.enabled = config.enabled ?? true;
    this.deviceType = config.deviceType ?? this.detectDeviceType();
    this.waitForExternalActorId = config.waitForExternalActorId ?? false;
    this.skipResolveWhenSessionValid = config.skipResolveWhenSessionValid ?? false;

    if (this.waitForExternalActorId && typeof window !== 'undefined') {
      this.listenForExternalActorId();
    }
  }

  /**
   * Initializes the personalization client.
   * Must be called before tracking events. If actor ID is already stored,
   * uses it immediately. Otherwise, resolves actor ID via the provided function.
   *
   * This method is idempotent - subsequent calls return the same promise.
   * Queued events are automatically flushed once initialization completes.
   *
   * @returns {Promise<void>} Promise that resolves when initialization is complete
   *
   * @example
   * await client.initialize();
   * // Client is now ready to track events
   */
  async initialize(): Promise<void> {
    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = this.doInitialize();
    return this.initializePromise;
  }

  /**
   * Re-initializes the client.
   * Useful when user logs in/out or switches accounts.
   * Fetches a new actor ID and updates storage.
   *
   * @returns {Promise<void>} Promise that resolves when re-initialization is complete
   *
   * @example
   * // User logs in
   * await client.reInitialize();
   *
   * // User logs out
   * await client.reInitialize();
   */
  async reInitialize(): Promise<void> {
    this.beginActorRefresh();
    const generation = ++this.reInitializeGeneration;
    try {
      const currentActorId = this.storage.getActorId() ?? undefined;
      const { actorId } = await this.resolveActorFn(currentActorId);
      if (generation !== this.reInitializeGeneration) {
        return; // A newer call superseded this one — discard stale result
      }
      this.storage.setActorId(actorId);
      this.setActorIdAndNotify(actorId);
    } catch {
      // silent fail — tracking must never break the app
    } finally {
      this.endActorRefresh();
    }
  }

  /**
   * Handles external logout (e.g., WordPress logout detected via cookie).
   * Clears all stored data (actorId, sessionId, sessionTimestamp)
   * and re-resolves to get a fresh anonymous actor ID.
   *
   * @returns {Promise<void>} Promise that resolves when re-initialization is complete
   */
  async handleExternalLogout(): Promise<void> {
    this.beginActorRefresh();
    this.storage.removeActorId();
    this.storage.removeSessionId();
    this.storage.removeSessionTimestamp();
    this.actorId = null;
    getOrCreateSessionId(this.storage);
    this.isInitialized = true;
    this.initializePromise = Promise.resolve();

    const generation = ++this.reInitializeGeneration;
    try {
      const { actorId } = await this.resolveActorFn();
      if (generation !== this.reInitializeGeneration) {
        return;
      }
      this.storage.setActorId(actorId);
      this.setActorIdAndNotify(actorId);
    } catch {
      // silent fail — tracking must never break the app
    } finally {
      this.endActorRefresh();
    }
  }

  /**
   * Tracks a personalization event.
   * Automatically adds session ID and device type to metadata.
   * Events are queued if actor ID is not yet available.
   *
   * @param {PersonalizationEvent} event - Event to track (discriminated union)
   *
   * @example
   * // Track a product view
   * client.track({
   *   eventType: 'view',
   *   productId: 'prod_123',
   *   context: 'product_detail',
   *   metadata: { position: 1 }
   * });
   *
   * @example
   * // Track add to cart
   * client.track({
   *   eventType: 'add_to_cart',
   *   productId: 'prod_123',
   *   context: 'product_detail',
   *   quantity: 2,
   *   metadata: { priceAtAdd: 49.99 }
   * });
   */
  track(event: PersonalizationEvent): Promise<void> {
    if (!this.enabled) {
      return Promise.resolve();
    }

    const sessionId = getOrCreateSessionId(this.storage);

    const payload: PersonalizationEventPayload = {
      actorId: this.actorId ?? '',
      eventType: event.eventType,
      productId: 'productId' in event ? event.productId : undefined,
      context: event.context,
      quantity: 'quantity' in event ? event.quantity : undefined,
      metadata: {
        ...('metadata' in event && event.metadata ? event.metadata : {}),
        sessionId,
        deviceType: this.deviceType,
      },
    };

    if (this.debug) {
      console.info('[Personalization]', payload.eventType, payload);
      return Promise.resolve();
    }

    if (!this.actorId || this.actorRefreshInFlight > 0) {
      return this.enqueue(payload);
    }

    return this.sendSafe(payload);
  }

  // === Convenience methods ===

  /**
   * Tracks a product view event.
   *
   * @param {string} productId - Unique product identifier
   * @param {ViewContext} context - Context where the view occurred (e.g., 'product_detail', 'search_results')
   * @param {ViewMetadata} [metadata] - Optional metadata (searchQuery, position, etc.)
   *
   * @example
   * client.view('prod_123', 'product_detail', {
   *   searchQuery: 'vitamin c serum',
   *   position: 1
   * });
   */
  view(productId: string, context: ViewContext, metadata?: ViewMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.VIEW, productId, context, metadata });
  }

  /**
   * Tracks a product click event.
   *
   * @param {string} productId - Unique product identifier
   * @param {ClickContext} context - Context where the click occurred
   * @param {ClickMetadata} [metadata] - Optional metadata (position, sectionName, etc.)
   *
   * @example
   * client.click('prod_123', 'recommendation_section', {
   *   position: 2,
   *   sectionName: 'You May Also Like'
   * });
   */
  click(productId: string, context: ClickContext, metadata?: ClickMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.CLICK, productId, context, metadata });
  }

  /**
   * Tracks a product impression event (when product appears in viewport).
   *
   * @param {string} productId - Unique product identifier
   * @param {ImpressionContext} context - Context where impression occurred
   * @param {ImpressionMetadata} [metadata] - Optional metadata (position is required)
   *
   * @example
   * client.impression('prod_123', 'search_results', {
   *   position: 3,
   *   totalItems: 20
   * });
   */
  impression(productId: string, context: ImpressionContext, metadata?: ImpressionMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.IMPRESSION, productId, context, metadata });
  }

  /**
   * Tracks an add to cart event.
   *
   * @param {string} productId - Unique product identifier
   * @param {AddToCartContext} context - Context where item was added to cart
   * @param {number} quantity - Quantity added
   * @param {AddToCartMetadata} [metadata] - Optional metadata (priceAtAdd, selectedVariant, etc.)
   *
   * @example
   * client.addToCart('prod_123', 'product_detail', 2, {
   *   priceAtAdd: 49.99,
   *   selectedVariant: 'size-large'
   * });
   */
  addToCart(productId: string, context: AddToCartContext, quantity: number, metadata?: AddToCartMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.ADD_TO_CART, productId, context, quantity, metadata });
  }

  /**
   * Tracks a remove from cart event.
   *
   * @param {string} productId - Unique product identifier
   * @param {RemoveFromCartContext} context - Context where item was removed
   * @param {number} [quantity] - Optional quantity removed
   *
   * @example
   * client.removeFromCart('prod_123', 'cart', 1);
   */
  removeFromCart(productId: string, context: RemoveFromCartContext, quantity?: number): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.REMOVE_FROM_CART, productId, context, quantity });
  }

  /**
   * Tracks a purchase event.
   *
   * @param {string} productId - Unique product identifier
   * @param {PurchaseContext} context - Context where purchase occurred
   * @param {number} quantity - Quantity purchased
   * @param {PurchaseMetadata} metadata - Required purchase metadata (orderId, revenue, currencyCode)
   *
   * @example
   * client.purchase('prod_123', 'checkout', 2, {
   *   orderId: 'order_456',
   *   revenue: 99.98,
   *   currencyCode: 'USD',
   *   discount: 10.00,
   *   couponCode: 'SAVE10',
   *   paymentMethod: 'credit_card'
   * });
   */
  purchase(productId: string, context: PurchaseContext, quantity: number, metadata: PurchaseMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.PURCHASE, productId, context, quantity, metadata });
  }

  /**
   * Tracks a user signup event.
   *
   * @param {SignupContext} context - Context where signup occurred
   * @param {SignupMetadata} [metadata] - Optional metadata (referralCode, etc.)
   *
   * @example
   * client.signup('registration_page', {
   *   referralCode: 'FRIEND123'
   * });
   */
  signup(context: SignupContext, metadata?: SignupMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.SIGNUP, context, metadata });
  }

  /**
   * Tracks a referral event (when user shares a referral code).
   *
   * @param {ReferralContext} context - Context where referral occurred
   * @param {ReferralMetadata} [metadata] - Optional metadata (referralCode, etc.)
   *
   * @example
   * client.referral('referral_page', {
   *   referralCode: 'USER123'
   * });
   */
  referral(context: ReferralContext, metadata?: ReferralMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.REFERRAL, context, metadata });
  }

  /**
   * Tracks a reward redemption event.
   *
   * @param {RewardRedemptionContext} context - Context where redemption occurred
   * @param {RewardRedemptionMetadata} metadata - Required metadata (rewardAmount)
   *
   * @example
   * client.rewardRedemption('rewards_page', {
   *   rewardAmount: 500
   * });
   */
  rewardRedemption(context: RewardRedemptionContext, metadata: RewardRedemptionMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.REWARD_REDEMPTION, context, metadata });
  }

  /**
   * Tracks a product review write event.
   *
   * @param {string} productId - Unique product identifier
   * @param {ReviewWriteContext} context - Context where review was written
   * @param {ReviewWriteMetadata} [metadata] - Optional metadata (rating, hasPhoto, orderId, etc.)
   *
   * @example
   * client.reviewWrite('prod_123', 'review_page', {
   *   rating: 5,
   *   hasPhoto: true,
   *   orderId: 'order_456'
   * });
   */
  reviewWrite(productId: string, context: ReviewWriteContext, metadata?: ReviewWriteMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.REVIEW_WRITE, productId, context, metadata });
  }

  /**
   * Tracks a home page view event.
   *
   * @param {HomePageViewContext} context - Context for the home page view
   * @param {HomePageViewMetadata} [metadata] - Optional metadata
   *
   * @example
   * client.homePageView('home');
   */
  homePageView(context: HomePageViewContext, metadata?: HomePageViewMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.HOME_PAGE_VIEW, context, metadata });
  }

  /**
   * Tracks a browsing event.
   *
   * @param {BrowsingContext} context - Context where browsing occurred
   * @param {BrowsingMetadata} [metadata] - Optional metadata
   *
   * @example
   * client.browsing('pdp');
   */
  browsing(context: BrowsingContext, metadata?: BrowsingMetadata): Promise<void> {
    return this.track({ eventType: EVENT_TYPE.BROWSING, context, metadata });
  }

  // === Subscription ===

  /**
   * Subscribes to actor ID changes.
   * Useful for syncing actor ID with external analytics tools.
   *
   * @param {ActorIdChangeListener} listener - Callback invoked when actor ID changes
   * @returns {() => void} Unsubscribe function
   *
   * @example
   * const unsubscribe = client.onActorIdChange((actorId) => {
   *   console.log('Actor ID changed:', actorId);
   *   // Sync with analytics tool
   *   analytics.identify(actorId);
   * });
   *
   * // Later, unsubscribe
   * unsubscribe();
   */
  onActorIdChange(listener: ActorIdChangeListener): () => void {
    this.actorIdChangeListeners = [...this.actorIdChangeListeners, listener];
    return () => {
      this.actorIdChangeListeners = this.actorIdChangeListeners.filter((l) => l !== listener);
    };
  }

  // === Getters ===

  /**
   * Checks if the client has completed initialization.
   *
   * @returns {boolean} True if initialized, false otherwise
   *
   * @example
   * if (client.getIsReady()) {
   *   client.view('prod_123', 'product_detail');
   * }
   */
  getIsReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Retrieves the current actor ID.
   *
   * @returns {string | null} Actor ID if available, null otherwise
   *
   * @example
   * const actorId = client.getActorId();
   * if (actorId) {
   *   console.log('Current user:', actorId);
   * }
   */
  getActorId(): string | null {
    return this.actorId;
  }

  /**
   * Retrieves the current session info (sessionId + timestamp).
   * Creates a new session if one doesn't exist yet.
   * Used by BridgeWebView to inject session data into WebView.
   */
  getSessionInfo(): { sessionId: string; sessionTimestamp: number } {
    const sessionId = getOrCreateSessionId(this.storage);
    return {
      sessionId,
      sessionTimestamp: this.storage.getSessionTimestamp() ?? Date.now(),
    };
  }

  /**
   * Cleans up all listeners and internal state.
   * Call this when the client is no longer needed (e.g., HMR teardown).
   */
  dispose(): void {
    this.externalActorIdAbortController?.abort();
    this.externalActorIdAbortController = null;
    this.actorIdChangeListeners = [];
    this.eventQueue = [];
  }

  // === Private ===

  private async doInitialize(): Promise<void> {
    // In WebView mode, skip resolveActor and wait for native injection
    if (this.waitForExternalActorId) {
      const stored = this.storage.getActorId();
      if (stored) {
        this.setActorIdAndNotify(stored);
      }
      this.isInitialized = true;
      this.flushEventQueue();
      return;
    }

    // Use cached actorId immediately for fast init, then revalidate in background
    const stored = this.storage.getActorId();
    if (stored) {
      this.setActorIdAndNotify(stored);
      getOrCreateSessionId(this.storage);
      const sessionTs = this.storage.getSessionTimestamp();
      const now = Date.now();
      const hasValidSession = Boolean(sessionTs && now - sessionTs < SESSION_TIMEOUT_MS);
      if (this.skipResolveWhenSessionValid && hasValidSession) {
        this.isInitialized = true;
        this.flushEventQueue();
        return;
      }
      this.isInitialized = true;
      this.flushEventQueue();
      this.resolveAndUpdate();
      return;
    }

    try {
      getOrCreateSessionId(this.storage);
      const { actorId } = await this.resolveActorFn(stored ?? undefined);
      this.storage.setActorId(actorId);
      this.setActorIdAndNotify(actorId);
    } catch {
      // silent fail — tracking must never break the app
    } finally {
      this.isInitialized = true;
      this.flushEventQueue();
    }
  }

  private resolveAndUpdate(): void {
    try {
      const result = this.resolveActorFn(this.actorId ?? undefined);
      if (result && typeof result.then === 'function') {
        result
          .then(({ actorId }) => {
            if (actorId !== this.actorId) {
              this.storage.setActorId(actorId);
              this.setActorIdAndNotify(actorId);
            }
          })
          .catch(() => {
            // silent fail — tracking must never break the app
          });
      }
    } catch {
      // silent fail — tracking must never break the app
    }
  }

  private enqueue(payload: PersonalizationEventPayload): Promise<void> {
    if (this.eventQueue.length >= EVENT_QUEUE_MAX_SIZE) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.eventQueue = [...this.eventQueue, { payload, resolve }];
    });
  }

  private flushEventQueue(): void {
    if (!this.actorId || this.eventQueue.length === 0) {
      return;
    }

    const queued = this.eventQueue;
    this.eventQueue = [];

    for (const { payload, resolve } of queued) {
      void this.sendSafe({ ...payload, actorId: this.actorId }).then(resolve);
    }
  }

  private async sendSafe(payload: PersonalizationEventPayload): Promise<void> {
    if (HIGH_VALUE_EVENT_TYPES.has(payload.eventType)) {
      return this.sendWithRetry(payload);
    }

    try {
      await this.sendEventFn(payload);
    } catch {
      // silent fail — tracking must never break the app
    }
  }

  private async sendWithRetry(payload: PersonalizationEventPayload, attempt = 1): Promise<void> {
    try {
      await this.sendEventFn(payload);
    } catch {
      if (attempt < RETRY_CONFIG.MAX_ATTEMPTS) {
        const delayMs = RETRY_CONFIG.BASE_DELAY_MS * Math.pow(2, attempt - 1);
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        return this.sendWithRetry(payload, attempt + 1);
      }
      // silent fail after max retries — tracking must never break the app
    }
  }

  private setActorIdAndNotify(actorId: string): void {
    this.actorId = actorId;
    for (const listener of this.actorIdChangeListeners) {
      try {
        listener(actorId);
      } catch {
        // silent fail
      }
    }
  }

  private beginActorRefresh(): void {
    this.actorRefreshInFlight += 1;
  }

  private endActorRefresh(): void {
    this.actorRefreshInFlight = Math.max(0, this.actorRefreshInFlight - 1);
    if (this.actorRefreshInFlight === 0) {
      this.flushEventQueue();
    }
  }

  private listenForExternalActorId(): void {
    this.externalActorIdAbortController?.abort();
    const controller = new AbortController();
    this.externalActorIdAbortController = controller;

    window.addEventListener('ss:actorIdInjected', ((event: CustomEvent<{
      actorId: string;
      sessionId?: string;
      sessionTimestamp?: number;
    }>) => {
      const { actorId, sessionId, sessionTimestamp } = event.detail;
      if (actorId) {
        this.storage.setActorId(actorId);
        this.setActorIdAndNotify(actorId);
      }
      if (sessionId) {
        this.storage.setSessionId(sessionId);
      }
      if (sessionTimestamp) {
        this.storage.setSessionTimestamp(sessionTimestamp);
      }
      this.flushEventQueue();
    }) as EventListener, { signal: controller.signal });
  }

  private detectDeviceType(): DeviceType {
    if (typeof window === 'undefined') {
      return DEVICE_TYPE.DESKTOP;
    }

    const width = window.innerWidth;
    if (width < 768) {
      return DEVICE_TYPE.MOBILE;
    }
    if (width < 1160) {
      return DEVICE_TYPE.TABLET;
    }
    return DEVICE_TYPE.DESKTOP;
  }
}
